import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, { pagerFor } from 'Containers/MCP/docs/DocsLayout';

import './reference.scss';

const PATH = '/docs/mcp/security';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#how-authorisation-works', label: 'How authorisation works' },
  { href: '#tokens', label: 'Token lifetime' },
  { href: '#what-the-agent-can-do', label: 'What the agent can do' },
  { href: '#publishing', label: 'Publishing' },
  { href: '#disconnecting', label: 'Disconnecting' },
];

const Security = () => (
  <DocsLayout
    eyebrow="Reference"
    title="Authentication & security"
    lede={
      <>
        The connector links Claude to your own Vocable account. This page
        explains how that link is authorised, what the agent is able to do once
        it exists, and how to end it.
      </>
    }
    stats={[
      { value: 'OAuth 2.1', label: 'with PKCE' },
      { value: '24 h', label: 'token lifetime' },
    ]}
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <div className="mcp-ref">
      <h2 id="how-authorisation-works">How the connection is authorised</h2>
      <p>
        Vocable&rsquo;s connector uses the OAuth 2.1 authorization-code flow with
        PKCE &mdash; the same pattern behind &ldquo;Sign in with&hellip;&rdquo;
        buttons across the web. In practice:
      </p>
      <ol>
        <li>
          You add the connector in Claude and Claude sends you to Vocable to
          sign in.
        </li>
        <li>
          You sign in to Vocable in your own browser and approve the connection
          there.
        </li>
        <li>
          Vocable hands Claude a short-lived access token that represents your
          approval.
        </li>
        <li>
          Claude uses that token &mdash; never your credentials &mdash; on every
          later request.
        </li>
      </ol>

      <div className="mcp-docs__callout">
        <p className="mcp-docs__callout-title">Claude never sees your password</p>
        <p>
          You type your Vocable password into Vocable, in your browser, and
          nowhere else. Claude only ever receives a token, and PKCE means that
          token can only be redeemed by the client that started the sign-in.
        </p>
      </div>

      <h2 id="tokens">Access tokens expire after 24 hours</h2>
      <p>
        An access token is valid for 24 hours. After that it stops working and
        the connector re-authorises &mdash; you may be asked to sign in to
        Vocable again. This is normal and is the reason a connector that worked
        yesterday can ask for attention today. If tools begin failing after a
        long gap, reconnecting is usually all that is needed.
      </p>

      <h2 id="what-the-agent-can-do">The agent acts as you</h2>
      <p>
        The connector does not create a separate, more powerful identity. Claude
        operates as your Vocable user: it can see and change exactly what your
        account can see and change, and it is held to the same limits &mdash;
        the same brands, the same team permissions, the same plan entitlements.
        It cannot reach another customer&rsquo;s data, and it cannot reach a
        brand your account has no access to.
      </p>
      <p>
        The flip side is that anything you could do in the Vocable app, the agent
        can do when you ask for it &mdash; including creating and updating
        documents. Treat a connected Claude conversation as a session logged into
        your account.
      </p>

      <h2 id="publishing">Publishing is the highest-impact capability</h2>
      <div className="mcp-docs__callout mcp-docs__callout--warn">
        <p className="mcp-docs__callout-title">
          Published content goes to your live integrations
        </p>
        <p>
          The publishing tools can push a document out to the platforms your
          brand has connected &mdash; a CMS, a social account. That content
          becomes visible to your real audience without a further stop in the
          Vocable app. It is the one capability worth being deliberate about.
        </p>
      </div>
      <p>
        Ask to see the draft before it goes anywhere. A useful habit is to
        request the document first, read it, and only then ask for it to be
        published &mdash; and to name the destination explicitly rather than
        leaving the choice open. The{' '}
        <Link to="/docs/mcp/tools#publishing">Publishing</Link> and{' '}
        <Link to="/docs/mcp/tools#documents">Documents</Link> sections of the
        tool reference list exactly which tools can write or publish.
      </p>

      <h2 id="reviewing-output">Review before it goes live</h2>
      <p>
        Generated content is a strong first draft, not a finished, fact-checked
        one. Before anything is published or sent to a client, read it yourself
        and check the claims, figures, names and links. Everything the agent
        creates stays visible in the Vocable app, so you can review, edit or
        delete it there as normal.
      </p>

      <h2 id="disconnecting">How to disconnect</h2>
      <p>
        You can end the connection at any time: open your connector settings in
        Claude and remove the Vocable connector. Claude immediately stops being
        able to reach your Vocable account, and the token it was holding is no
        longer usable. Removing the connector does not delete anything in
        Vocable &mdash; your brands, documents and content stay exactly as they
        are.
      </p>
      <p>
        To reconnect later, add the connector again and sign in when prompted.
        See the <Link to="/docs/mcp/quickstart">Quickstart</Link> for the setup
        steps, or{' '}
        <Link to="/docs/mcp/troubleshooting">Troubleshooting &amp; FAQ</Link> if
        something is not behaving as expected.
      </p>
    </div>
  </DocsLayout>
);

export default Security;
